import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { Typography } from '@pflegenavi/web-components';
import type { PayoutReconciliationEntry } from '@pflegenavi/service-provider-frontend/api';
import { useGetPayoutReconciliation } from '@pflegenavi/service-provider-frontend/api';
import {
  csvDownload,
  useGetBatchReceiptDateString,
} from '@pflegenavi/shared/frontend';
import { dateAsYYYY_MM_DD } from '@pflegenavi/shared/utils';

interface PayoutReconciliationDialogProps {
  open: boolean;
  onClose: () => void;
  payoutId: string;
  initiationDate: Date;
}

export const PayoutReconciliationDialog: FC<
  PayoutReconciliationDialogProps
> = ({ open, onClose, payoutId, initiationDate }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);
  const { getBatchReceiptDateString } = useGetBatchReceiptDateString();

  const { refetch } = useGetPayoutReconciliation(payoutId, { enabled: false });

  const formatDetails = useCallback(
    (entry: PayoutReconciliationEntry): string => {
      const minDate = entry.batch_min_receipt_date
        ? new Date(entry.batch_min_receipt_date)
        : undefined;
      const maxDate = entry.batch_max_receipt_date
        ? new Date(entry.batch_max_receipt_date)
        : undefined;
      const batchDate = entry.receipt_batch_entry?.receipt_batch?.receipt_date
        ? new Date(entry.receipt_batch_entry.receipt_batch.receipt_date)
        : undefined;

      return getBatchReceiptDateString(true, minDate, maxDate, batchDate);
    },
    [getBatchReceiptDateString]
  );

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    try {
      const result = await refetch();
      if (result.error) {
        enqueueSnackbar(t('dialog.payout-reconciliation.download-error'), {
          variant: 'error',
        });
        return;
      }
      if (result.data) {
        const csvData = result.data.data.map((entry) => ({
          // Institution & Tenant first
          nursing_home_name: entry.nursing_home?.nursing_home_name ?? '',
          nursing_home_tenant: entry.nursing_home?.tenant ?? '',
          // Date & Money
          performed_at: entry.performed_at,
          amount: entry.amount / 100,
          currency: entry.currency,
          // Resident info
          resident_firstname:
            entry.receipt_batch_entry?.resident?.firstname ?? '',
          resident_lastname:
            entry.receipt_batch_entry?.resident?.lastname ?? '',
          // Batch info
          receipt_batch_id: entry.receipt_batch_entry?.receipt_batch?.id ?? '',
          batch_entry_count: entry.batch_entry_count ?? '',
          batch_accepted_on:
            entry.receipt_batch_entry?.receipt_batch?.accepted_on ?? '',
          // Transaction details
          transaction_type: entry.mangopay_transaction_type,
          details: formatDetails(entry),
        }));

        csvDownload({
          data: csvData,
          filename: `payout-reconciliation-${dateAsYYYY_MM_DD(
            initiationDate
          )}.csv`,
          headers: [
            // Institution & Tenant first
            t('csv.payout-reconciliation.headers.nursing-home-name'),
            t('csv.payout-reconciliation.headers.nursing-home-tenant'),
            // Date & Money
            t('csv.payout-reconciliation.headers.performed-at'),
            t('csv.payout-reconciliation.headers.amount'),
            t('csv.payout-reconciliation.headers.currency'),
            // Resident info
            t('csv.payout-reconciliation.headers.resident-firstname'),
            t('csv.payout-reconciliation.headers.resident-lastname'),
            // Batch info
            t('csv.payout-reconciliation.headers.receipt-batch-id'),
            t('csv.payout-reconciliation.headers.batch-entry-count'),
            t('csv.payout-reconciliation.headers.batch-accepted-on'),
            // Transaction details
            t('csv.payout-reconciliation.headers.transaction-type'),
            t('csv.payout-reconciliation.headers.details'),
          ],
        });
        onClose();
      }
    } finally {
      setIsDownloading(false);
    }
  }, [refetch, initiationDate, t, onClose, enqueueSnackbar, formatDetails]);

  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        <Typography variant="h5">
          {t('dialog.payout-reconciliation.title')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          {t('dialog.payout-reconciliation.content')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('dialog.payout-reconciliation.cancel')}
        </Button>
        <LoadingButton
          loading={isDownloading}
          onClick={handleDownload}
          variant="contained"
        >
          {t('dialog.payout-reconciliation.download')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
